import { BASE_NOTES, MUSIC_SCALES } from '../utils/constants.js';

/**
 * Shared song-format expansion — the ONE place that understands how a saved
 * composition maps to pitches and steps. Both playback consumers (the flock
 * performance and the ship radio) must use this instead of hand-rolling
 * grid math, so a song always sounds exactly as composed.
 *
 * Formats:
 *  - v1: notes are "col:row" strings on a fixed 32×14 grid, base octave 3.
 *  - v2: notes are { col, row, dur } objects; grid is bars×16 columns and
 *        (octaves × scale length) rows; base octave = planet's octave.
 */

export const CELLS_PER_BAR = 16;
const V1_COLS = 32;
const V1_ROWS = 14;

/**
 * Drum lanes for the percussion track — row 0 at the top of the grid.
 * Each lane's note sits squarely inside one of DrumKit's pitch buckets,
 * so the mapping works through the ordinary triggerAttackRelease path.
 */
export const DRUM_LANES = [
  { label: 'Shaker',   note: 'C6'  },
  { label: 'Open Hat', note: 'A4'  },
  { label: 'Hi-Hat',   note: 'E4'  },
  { label: 'Clap',     note: 'C4'  },
  { label: 'Rim',      note: 'G#3' },
  { label: 'Tom',      note: 'E3'  },
  { label: 'Snare',    note: 'C3'  },
  { label: 'Kick',     note: 'C2'  },
];

export function drumNoteForRow(row) {
  const lane = DRUM_LANES[Math.max(0, Math.min(DRUM_LANES.length - 1, row | 0))];
  return lane.note;
}

/** Mirror of MusicMaker._buildScale — row 0 is the HIGHEST pitch. */
export function buildScaleNotes(key, scaleName, rows, baseOctave = 3) {
  const rootIdx = Math.max(BASE_NOTES.indexOf(key || 'C'), 0);
  const scale = MUSIC_SCALES[scaleName] || MUSIC_SCALES.pentatonic;
  const notes = [];
  for (let i = 0; i < rows; i++) {
    const semitone = scale[i % scale.length];
    const octOff = Math.floor(i / scale.length);
    notes.push(BASE_NOTES[(rootIdx + semitone) % 12] + (baseOctave + octOff));
  }
  return notes.reverse();
}

/** Accepts a v1 "col:row" string or a v2 {col,row,dur} object. */
export function parseCell(cell) {
  if (cell == null) return null;
  if (typeof cell === 'string') {
    const [col, row] = cell.split(':').map(Number);
    if (Number.isNaN(col) || Number.isNaN(row)) return null;
    return { col, row, dur: 1 };
  }
  if (typeof cell === 'object' && Number.isFinite(cell.col) && Number.isFinite(cell.row)) {
    return {
      col: cell.col,
      row: cell.row,
      dur: Math.max(1, cell.dur || 1),
      vel: typeof cell.v === 'number' ? cell.v : (typeof cell.vel === 'number' ? cell.vel : 1),
    };
  }
  return null;
}

/**
 * Expand a saved song into a playable structure.
 * @param {object} songData { tracks, bpm, key, scaleName, bars?, octaves?, octave? }
 * @param {number} [baseOctaveFallback] planet.music.octave for v2 docs saved
 *                 before the octave field existed
 * @returns {null | {
 *   bpm, stepDur, cols, scaleNotes,
 *   tracks: [{ instrumentRole, name, hasCells, events: [{step, note, durSteps}] }]
 * }}
 */
export function expandSong(songData, baseOctaveFallback = 3) {
  if (!songData?.tracks?.length) return null;

  // Detect format from the first cell found anywhere
  let sampleCell = null;
  for (const t of songData.tracks) {
    if (t?.notes?.length) { sampleCell = t.notes[0]; break; }
  }
  if (sampleCell == null) return null;
  const isV1 = typeof sampleCell === 'string';

  const bpm = songData.bpm || 100;
  const scale = MUSIC_SCALES[songData.scaleName] || MUSIC_SCALES.pentatonic;
  const rows = isV1 ? V1_ROWS : (songData.octaves || 3) * scale.length;
  const baseOctave = isV1 ? 3 : (songData.octave || baseOctaveFallback || 3);
  const scaleNotes = buildScaleNotes(songData.key, songData.scaleName, rows, baseOctave);
  const gridCols = isV1 ? V1_COLS : (songData.bars || 8) * CELLS_PER_BAR;

  let maxStepUsed = 0;
  const tracks = songData.tracks.map(t => {
    // v2 percussion rows are drum lanes, not pitches (v1 predates the drum lane)
    const isDrums = !isV1 && t?.instrumentRole === 'percussion';
    const events = [];
    for (const cell of t?.notes || []) {
      const p = parseCell(cell);
      if (!p || p.col < 0 || p.col >= gridCols) continue;
      const note = isDrums ? drumNoteForRow(p.row) : scaleNotes[p.row];
      if (!note) continue;
      events.push({ step: p.col, note, durSteps: p.dur, vel: p.vel ?? 1 });
      maxStepUsed = Math.max(maxStepUsed, p.col + p.dur);
    }
    return {
      instrumentRole: t?.instrumentRole || 'keys',
      name: t?.name || '',
      hasCells: !!t?.notes?.length,
      volume: typeof t?.volume === 'number' ? t.volume : 0.8,
      events,
    };
  });

  // Trim trailing empty bars so default-length grids don't add dead air,
  // but always keep at least one full bar.
  const cols = Math.max(
    CELLS_PER_BAR,
    Math.min(gridCols, Math.ceil(maxStepUsed / CELLS_PER_BAR) * CELLS_PER_BAR),
  );

  return { bpm, stepDur: 60 / bpm / 4, cols, scaleNotes, tracks };
}
